import React from "react";
import { motion } from "framer-motion";
import { Target, Eye, Quote, History, Building2 } from "lucide-react";
import { SCHOOL, IMAGES } from "@/lib/constants";
import { PageBanner } from "@/components/PageBanner";
import { SectionHeading } from "@/components/SectionHeading";

export default function About() {
  return (
    <div>
      <PageBanner title="About Us" subtitle="Discover the story, vision and values behind B.R.Smart Kidz / Academy." image={IMAGES.students} />

      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <img src={IMAGES.smartClass} alt="classroom" className="rounded-3xl shadow-xl w-full object-cover" />
        <div>
          <SectionHeading label="Our Introduction" title="Smart learning, strong values" />
          <p className="text-slate-600 leading-relaxed">
            B.R.Smart Kidz / Academy is a co-educational, English medium school in the heart of Kolkata, following the CBSE curriculum. Established on <b className="text-navy">{SCHOOL.established}</b>, we offer education from Pre-Nursery right up to Class 10.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            Our air-conditioned smart classrooms, computer & science labs, and dedicated faculty create an environment where children learn joyfully and grow into confident, responsible citizens. We are proudly supported by <b className="text-navy">{SCHOOL.foundation}</b>, established in {SCHOOL.foundationYear} to advance quality education in our community.
          </p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-[#F3F4F6] py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          {[
            { icon: Eye, color: "#E63946", title: "Our Vision", text: "To be a nurturing centre of excellence where every child discovers their potential and develops a lifelong love for learning, rooted in strong moral values." },
            { icon: Target, color: "#2A9D8F", title: "Our Mission", text: "To provide holistic, joyful and value-based education through modern teaching methods, individual attention and a safe, hygienic environment that inspires curiosity and confidence." },
          ].map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm">
              <div className="h-14 w-14 rounded-2xl grid place-items-center mb-5" style={{ backgroundColor: `${c.color}1a`, color: c.color }}>
                <c.icon className="h-7 w-7" />
              </div>
              <h3 className="font-head font-extrabold text-2xl text-navy">{c.title}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Principal message */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-3 gap-10 items-start">
        <div className="lg:col-span-1">
          <div className="bg-navy rounded-3xl p-8 text-center text-white shadow-xl">
            <div className="h-24 w-24 rounded-full bg-gold/20 grid place-items-center mx-auto mb-4">
              <Quote className="h-10 w-10 text-gold" />
            </div>
            <h4 className="font-head font-bold text-xl">The Principal</h4>
            <p className="text-sm text-slate-300 mt-1">B.R.Smart Kidz / Academy</p>
          </div>
        </div>
        <div className="lg:col-span-2">
          <SectionHeading label="Director / Principal's Message" title="Every child is a promise" />
          <p className="text-slate-600 leading-relaxed">
            "Dear Parents and Well-wishers, welcome to our school family. We believe that every child is unique and full of promise. Our role is to unlock that promise through love, discipline and modern learning. At B.R.Smart Academy, we don't just teach subjects — we shape character, build confidence and prepare children for a bright future."
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            "We warmly invite you to join our journey and give your child the gift of a strong, values-driven foundation."
          </p>
          <p className="mt-4 font-head font-bold text-navy">— Principal, B.R.Smart Kidz / Academy</p>
        </div>
      </section>

      {/* History timeline */}
      <section className="bg-navy py-20 text-white">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading center light label="Our Journey" title="School History" />
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: History, year: "2018", title: "The Beginning", text: "B.R.Smart Kidz / Academy was established on 2 July 2018 with a vision to bring quality CBSE English medium education to Kolkata." },
              { icon: Building2, year: "2021", title: "BRSK Foundation", text: "The BRSK Foundation was established to support and expand accessible, quality education initiatives." },
            ].map((h) => (
              <div key={h.year} className="bg-white/5 border border-white/10 rounded-3xl p-7">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-xl bg-gold/20 grid place-items-center text-gold"><h.icon className="h-6 w-6" /></div>
                  <span className="font-head font-black text-3xl text-gold">{h.year}</span>
                </div>
                <h4 className="font-head font-bold text-lg">{h.title}</h4>
                <p className="text-slate-300 text-sm mt-2 leading-relaxed">{h.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
